import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeTempId,
  buildItemLocalizedDetails,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getChoiceDisplayTitle,
  getItemDisplayTitle,
} from "./utils";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import {
  formatMoney,
  getCurrencySymbol,
  getRestaurantCurrencyCode,
} from "../../../../../utils/currency";

function countChoicesForItem(itemId, choiceItems) {
  let count = 0;
  Object.values(choiceItems || {}).forEach((ids) => {
    if (Array.isArray(ids) && ids.includes(itemId)) count += 1;
  });
  return count;
}

const Step4Items = ({ draft, setDraft, goNext, goPrev }) => {
  const { t } = useTranslation();
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const choices = Array.isArray(draft?.choices) ? draft.choices : [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const choiceItems = draft?.relations?.choiceItems || {};
  const restaurantCurrency = getRestaurantCurrencyCode();
  const currencySymbol = getCurrencySymbol(restaurantCurrency);

  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [havePrice, setHavePrice] = useState(true);
  const [editingItemId, setEditingItemId] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const itemFormRef = useRef(null);

  const toggleItemSelection = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const getAssignableItemIds = (choiceId, sourceIds) => {
    const assignedOnChoice = new Set(choiceItems[choiceId] || []);
    return [...new Set((sourceIds || []).filter(Boolean))].filter(
      (itemId) => !assignedOnChoice.has(itemId)
    );
  };

  const assignItemsToChoice = (choiceId, itemIds) => {
    const uniqueIds = getAssignableItemIds(choiceId, itemIds);
    if (!uniqueIds.length || !choiceId) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextChoiceItems = { ...(rel.choiceItems || {}) };
      const current = new Set(nextChoiceItems[choiceId] || []);
      uniqueIds.forEach((id) => current.add(id));
      nextChoiceItems[choiceId] = [...current];
      return {
        ...prev,
        relations: { ...rel, choiceItems: nextChoiceItems },
      };
    });
    showWizardToast(t, "wizardItemsAssigned", { count: uniqueIds.length });
    setSelectedItemIds(new Set());
  };

  const unassignItemFromChoice = (choiceId, itemId) => {
    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextChoiceItems = { ...(rel.choiceItems || {}) };
      const filtered = (nextChoiceItems[choiceId] || []).filter((id) => id !== itemId);
      if (filtered.length) nextChoiceItems[choiceId] = filtered;
      else delete nextChoiceItems[choiceId];
      return {
        ...prev,
        relations: { ...rel, choiceItems: nextChoiceItems },
      };
    });
  };

  const canSave = Boolean(title.trim());

  const resetItemForm = () => {
    setTitle("");
    setPrice("");
    setHavePrice(true);
    setEditingItemId(null);
  };

  useEffect(() => {
    if (!editingItemId || !itemFormRef.current) return;
    itemFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    document.getElementById("wizard-item-title")?.focus({ preventScroll: true });
  }, [editingItemId]);

  const startEditItem = (item) => {
    setEditingItemId(item.tempId);
    setTitle(getItemDisplayTitle(item, menuPrimaryLanguage) || "");
    setHavePrice(item.havePrice !== false);
    setPrice(item.havePrice !== false ? String(item.price ?? 0) : "");
  };

  const saveItem = () => {
    if (!title.trim()) {
      showWizardValidation(t, "wizardItemTitleRequired");
      return;
    }

    const itemDetails = buildItemLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles: { [menuPrimaryLanguage]: title },
    });

    const itemPayload = {
      havePrice,
      price: havePrice ? Number(price || 0) : 0,
      itemDetails,
    };

    if (editingItemId) {
      setDraft((prev) => ({
        ...prev,
        items: (prev.items || []).map((item) =>
          item.tempId === editingItemId ? { ...item, ...itemPayload } : item
        ),
      }));
      showWizardToast(t, "wizardItemUpdated");
    } else {
      setDraft((prev) => ({
        ...prev,
        items: [
          ...(Array.isArray(prev.items) ? prev.items : []),
          {
            tempId: makeTempId("item"),
            ...itemPayload,
          },
        ],
      }));
      showWizardToast(t, "wizardItemAdded");
    }

    resetItemForm();
  };

  const removeItem = (itemTempId) => {
    if (editingItemId === itemTempId) {
      resetItemForm();
    }
    setDraft((prev) => {
      const next = { ...prev };
      next.items = (prev.items || []).filter((it) => it.tempId !== itemTempId);

      const nextRelations = { ...(prev.relations || {}) };
      nextRelations.choiceItems = { ...(nextRelations.choiceItems || {}) };
      for (const [choiceTempId, itemTempIds] of Object.entries(nextRelations.choiceItems)) {
        if (!Array.isArray(itemTempIds)) continue;
        const filtered = itemTempIds.filter((id) => id !== itemTempId);
        if (filtered.length) nextRelations.choiceItems[choiceTempId] = filtered;
        else delete nextRelations.choiceItems[choiceTempId];
      }

      next.relations = nextRelations;
      return next;
    });
    showWizardToast(t, "wizardItemDeleted");
  };

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">5</span>
            {t("wizardItemsHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardItemsSub")}</p>
        </div>

        <div className="start-options">
          <section
            className="start-products-form start-step-hero-scroll-target"
            ref={itemFormRef}
            aria-label={editingItemId ? t("wizardEditItemLabel") : t("wizardAddItemLabel")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <h3 className="start-options-section-head__title">
                {editingItemId ? t("wizardEditItemLabel") : t("wizardAddItemLabel")}
              </h3>
            </div>
            <div className="start-item-fields">
              <div>
                <label className="start-step1-label" htmlFor="wizard-item-title">
                  {t("wizardItemTitleLabel")}
                  <span className="start-step1-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="wizard-item-title"
                  className="form-control start-input"
                  placeholder={t("wizardItemTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-item-price">
                  {t("price")}
                  <span className="start-step1-label__lang">{restaurantCurrency}</span>
                </label>
                <div className={`input-group start-price-input-group${!havePrice ? " is-disabled" : ""}`}>
                  <span className="input-group-text start-price-input-prefix">{currencySymbol}</span>
                  <input
                    id="wizard-item-price"
                    type="number"
                    className="form-control start-input start-price-input"
                    value={price}
                    disabled={!havePrice}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
                <label
                  className={`start-item-price-toggle${!havePrice ? " is-on" : ""}`}
                  htmlFor="wizard-item-no-price"
                >
                  <span>{t("wizardItemNoExtraPrice")}</span>
                  <span className="form-check form-switch m-0">
                    <input
                      id="wizard-item-no-price"
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={!havePrice}
                      onChange={(e) => {
                        const noExtraPrice = e.target.checked;
                        setHavePrice(!noExtraPrice);
                        if (noExtraPrice) setPrice("");
                      }}
                    />
                  </span>
                </label>
              </div>
            </div>
            <div className="start-products-form__actions">
              <button
                type="button"
                className="start-products-add"
                disabled={!canSave}
                onClick={saveItem}
              >
                {editingItemId ? t("wizardUpdateItem") : t("wizardAddItem")}
              </button>
              {editingItemId ? (
                <button type="button" className="start-static-btn" onClick={resetItemForm}>
                  {t("cancel")}
                </button>
              ) : null}
            </div>
          </section>

          <section className="start-products-added" aria-label={t("wizardAllItems")}>
            <div className="start-products-added__head">
              <h3 className="start-products-added__title">{t("wizardAllItems")}</h3>
              <span className="start-products-added__count">{items.length}</span>
            </div>
            <p className="start-options-hint">{t("wizardItemsMultiChoiceHint")}</p>
            {items.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoItemsYet")}</p>
            ) : (
              <ul className="start-products-rows">
                {items.map((item) => {
                  const isSelected = selectedItemIds.has(item.tempId);
                  const choiceCount = countChoicesForItem(item.tempId, choiceItems);
                  return (
                    <li
                      key={item.tempId}
                      className={`start-products-row start-option-row${isSelected ? " is-selected" : ""}${
                        editingItemId === item.tempId ? " is-editing" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="start-option-row__select"
                        onClick={() => toggleItemSelection(item.tempId)}
                      >
                        <span className={`start-option-row__check${isSelected ? " is-on" : ""}`}>
                          {isSelected ? <i className="fa fa-check" aria-hidden="true" /> : null}
                        </span>
                        <span className="start-products-row__main">
                          <span className="start-products-row__title">
                            {getItemDisplayTitle(item, menuPrimaryLanguage)}
                          </span>
                          <span className="start-option-row__meta">
                            {item.havePrice
                              ? formatMoney(item.price ?? 0)
                              : t("wizardItemNoExtraPrice")}
                            {choiceCount > 0
                              ? ` · ${t("wizardItemChoiceCount", { count: choiceCount })}`
                              : ""}
                          </span>
                        </span>
                      </button>
                      <div className="start-products-row__actions">
                        <button
                          type="button"
                          className="start-products-row__icon-btn is-edit"
                          onClick={() => startEditItem(item)}
                          aria-label={t("edit")}
                          title={t("edit")}
                        >
                          <i className="fa fa-pen" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="start-products-row__icon-btn is-danger"
                          onClick={() => removeItem(item.tempId)}
                          aria-label={t("remove")}
                          title={t("remove")}
                        >
                          <i className="fa fa-trash-alt" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="start-products-added" aria-label={t("wizardAssignItemsToChoices")}>
            <div className="start-products-added__head">
              <h3 className="start-products-added__title">{t("wizardAssignItemsToChoices")}</h3>
              {selectedItemIds.size > 0 ? (
                <span className="start-products-added__count">{selectedItemIds.size}</span>
              ) : null}
            </div>
            {choices.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoChoicesForItems")}</p>
            ) : (
              <div className="start-assign-list">
                {choices.map((choice) => {
                  const assignedIds = choiceItems[choice.tempId] || [];
                  const hasSelection = selectedItemIds.size > 0;
                  const sourceIds = [...selectedItemIds];
                  const pendingIds = hasSelection
                    ? getAssignableItemIds(choice.tempId, sourceIds)
                    : [];
                  const canAssign = hasSelection && pendingIds.length > 0;

                  return (
                    <article key={choice.tempId} className="start-assign-card">
                      <div className="start-assign-card__head">
                        <div className="start-products-row__main">
                          <span className="start-assign-card__name">
                            {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                          </span>
                          <span className="start-option-row__meta">
                            {t("wizardChoiceMinLabel")} {choice.minSelected ?? 0}
                            {" · "}
                            {t("wizardChoiceMaxLabel")} {choice.maxSelected ?? 1}
                          </span>
                        </div>
                        {items.length > 0 ? (
                          <button
                            type="button"
                            className="start-assign-btn"
                            disabled={!canAssign}
                            onClick={() => assignItemsToChoice(choice.tempId, sourceIds)}
                          >
                            {!hasSelection
                              ? t("wizardAssignItemsBatch", { count: 0 })
                              : pendingIds.length > 0
                                ? t("wizardAssignItemsBatch", { count: pendingIds.length })
                                : t("wizardItemsAlreadyOnChoice")}
                          </button>
                        ) : null}
                      </div>
                      <div className="start-assign-chips">
                        {assignedIds.length === 0 ? (
                          <p className="start-assign-empty">{t("wizardNoItemsYet")}</p>
                        ) : (
                          assignedIds.map((itemId) => {
                            const item = items.find((it) => it.tempId === itemId);
                            if (!item) return null;
                            return (
                              <span key={itemId} className="start-assign-chip">
                                <i className="fa fa-utensils" aria-hidden="true" />
                                <span className="start-assign-chip__label">
                                  {getItemDisplayTitle(item, menuPrimaryLanguage)}
                                </span>
                                {item.havePrice ? (
                                  <em>{formatMoney(item.price ?? 0)}</em>
                                ) : null}
                                <button
                                  type="button"
                                  className="start-assign-chip__remove"
                                  onClick={() => unassignItemFromChoice(choice.tempId, itemId)}
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-times" aria-hidden="true" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep5of9")}</div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary sw-btn-skip" onClick={goNext} type="button">
            {t("wizardSkipStep")}
          </button>
          <button className="btn btn-primary sw-btn-next start-btn-next" onClick={goNext} type="button">
            {t("wizardContinue")} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Step4Items;
